/**
 * Indigo FlexibleDatePicker for the design system.
 *
 * A date picker that captures how precisely the date is known — a whole year, a
 * month, or an exact day — instead of forcing every answer into `MM/DD/YYYY`.
 * The precision dropdown sits at the trailing edge of the label row and drives
 * the calendar's mode.
 *
 * Value/precision travel as a pair, matching the GraphQL `FlexibleDate` type.
 * Typed on plain strings rather than the generated `DatePrecision` enum so the
 * design system stays decoupled from `~/api` (`DatePrecision.YEAR` still assigns
 * cleanly — string enum members are subtypes of their literal).
 *
 * See `specs/flexible-partial-dates-migration.md` (GET-910).
 */
import { DatePicker as AntDatePicker } from "antd";
import type { SizeType } from "antd/es/config-provider/SizeContext";
import { clsx } from "clsx";
import dayjs, { Dayjs } from "dayjs";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { hasActiveTextSelection } from "~/utils/textInputUtils";
import { DropdownSelector } from "../DropdownSelector";
import { LabelBlock } from "../LabelBlock";
import { Tooltip } from "../Tooltip";
import "../DatePicker/DatePicker.css";
import "./FlexibleDatePicker.css";

/** The precisions this control can capture. Mirrors `DatePrecision` minus PRESENT. */
export type FlexibleDatePrecision = "YEAR" | "MONTH" | "DAY";

type PrecisionSpec = {
  /** Characters of a `YYYY-MM-DD` string this precision keeps. */
  length: number;
  antPicker: "year" | "month" | "date";
  /** Both the parse format and the display format — they're the same shape. */
  format: string;
  label: string;
};

const PRECISIONS: Record<FlexibleDatePrecision, PrecisionSpec> = {
  YEAR: { length: 4, antPicker: "year", format: "YYYY", label: "Year" },
  MONTH: { length: 7, antPicker: "month", format: "MM/YYYY", label: "Month" },
  DAY: { length: 10, antPicker: "date", format: "MM/DD/YYYY", label: "Date" },
};

const PRECISION_OPTIONS = (
  Object.keys(PRECISIONS) as FlexibleDatePrecision[]
).map((value) => ({ value, label: PRECISIONS[value].label }));

/** Storage form for a precision: `2019`, `2019-05`, `2019-05-14`. */
const storageFormat = (precision: FlexibleDatePrecision): string =>
  "YYYY-MM-DD".slice(0, PRECISIONS[precision].length);

/**
 * Drop the parts a coarser precision doesn't hold: `2019-05-14` → `2019` at YEAR.
 * Null when the value is too coarse to satisfy `precision` — we can't invent a
 * month the patient never gave.
 */
export const truncate = (
  value: string | null,
  precision: FlexibleDatePrecision,
): string | null => {
  const { length } = PRECISIONS[precision];
  if (value === null || value.length < length) return null;
  return value.slice(0, length);
};

export interface FlexibleDatePickerProps {
  /** Stored value at `precision` — `2019`, `2019-05`, or `2019-05-14`. */
  value?: string | null;

  /**
   * How precisely the date is known. Drives the calendar mode. Fully controlled:
   * the consumer must persist what `onChange` reports **including when the value
   * is null**, since picking "Month" before entering one is a real selection.
   */
  precision?: FlexibleDatePrecision;

  /**
   * Fired on both a date pick and a precision switch. `value` is null when the
   * new precision is finer than what's known — the pair is still meaningful and
   * must be stored, not collapsed.
   */
  onChange?: (value: string | null, precision: FlexibleDatePrecision) => void;

  /** Display mode — renders the formatted value as click-to-copy text. */
  disabled?: boolean;

  /** Label text displayed above the picker. */
  label?: string;

  /** Description text displayed alongside the picker. */
  description?: string;

  /** Whether the picker is in an error state. */
  isError?: boolean;

  /** Fired when the picker gains focus — useful for clearing error states. */
  onFocus?: () => void;

  /** Width of the container as a percentage. */
  widthPercent?: string;

  /** Placeholder text when no date is selected. */
  placeholder?: string;

  /** Test ID for the date picker element. */
  dateTestId?: string;

  /** Test ID for the date picker popup. */
  datePopupTestId?: string;

  /** Test ID for the precision dropdown. */
  precisionTestId?: string;

  /** Additional CSS class for the picker. */
  className?: string;

  /**
   * Size of the control — "small" 24px, "default" 32px, "large" 40px.
   * @default "default"
   */
  size?: "small" | "default" | "large";

  /** Tooltip text shown on hover of a "?" icon next to the label (edit mode only). */
  infoTooltip?: string;

  /** Show the label tooltip in display mode as well as edit mode. */
  alwaysShowInfoTooltip?: boolean;
}

export const FlexibleDatePicker: FC<FlexibleDatePickerProps> = ({
  value = null,
  precision = "DAY",
  onChange,
  disabled = false,
  label,
  description,
  isError = false,
  onFocus,
  widthPercent = "auto",
  placeholder = "Select date",
  dateTestId,
  datePopupTestId,
  precisionTestId,
  className = "",
  size = "default",
  infoTooltip,
  alwaysShowInfoTooltip = false,
}) => {
  const spec = PRECISIONS[precision];

  // Where to open the calendar when there's no value to open on. Year 2024 →
  // Month lands the panel on 2024 rather than today, so the month is one click
  // away. Only ever positions the panel — it is never emitted, because a month
  // the patient didn't give must not become a stored value.
  const [anchor, setAnchor] = useState<string | null>(value);
  useEffect(() => {
    if (value !== null) setAnchor(value);
  }, [value]);

  const date: Dayjs | null = useMemo(
    () => (value ? dayjs(value, storageFormat(precision)) : null),
    [value, precision],
  );

  // Anchors are read at whatever precision produced them, so a YEAR anchor still
  // resolves to a real day for the panel to sit on.
  const anchorDate: Dayjs | null = useMemo(() => {
    const parsed = anchor ? dayjs(anchor) : null;
    return parsed?.isValid() ? parsed : null;
  }, [anchor]);

  const formattedDate = date?.isValid() ? date.format(spec.format) : null;

  const handleDateChange = useCallback(
    (picked: Dayjs | null) => {
      const next = picked ? picked.format(storageFormat(precision)) : null;
      setAnchor(next);
      onChange?.(next, precision);
    },
    [onChange, precision],
  );

  const handlePrecisionChange = useCallback(
    (next: FlexibleDatePrecision | null) => {
      if (next === null) return;
      // Coarsening truncates; refining clears. A day the user already coarsened
      // away doesn't come back — that would behave differently before and after
      // a reload, where the stored value is all there is.
      const truncated = truncate(value, next);
      if (value !== null) setAnchor(value);
      onChange?.(truncated, next);
    },
    [onChange, value],
  );

  const antSize: SizeType =
    size === "small" ? "small" : size === "large" ? "large" : "middle";

  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);

  // Display mode: text only, no precision control — there's nothing to change.
  if (disabled) {
    const handleCopyOnClick = () => {
      if (hasActiveTextSelection() || !formattedDate) return;
      navigator.clipboard
        .writeText(formattedDate)
        .then(() => {
          setShowCopiedTooltip(true);
          setTimeout(() => setShowCopiedTooltip(false), 2000);
        })
        .catch(() => {
          // Silently fail if clipboard access is denied
        });
    };

    return (
      <LabelBlock
        label={label}
        description={description}
        disabled
        widthPercent={widthPercent}
        infoTooltip={infoTooltip}
        alwaysShowInfoTooltip={alwaysShowInfoTooltip}
      >
        <Tooltip
          content="Copied!"
          placement="top"
          size="sm"
          visible={showCopiedTooltip}
        >
          <div
            className="indigo-datepicker-disabled-text"
            onClick={handleCopyOnClick}
          >
            {formattedDate || "-"}
          </div>
        </Tooltip>
      </LabelBlock>
    );
  }

  return (
    <LabelBlock
      label={label}
      description={description}
      isError={isError}
      widthPercent={widthPercent}
      infoTooltip={infoTooltip}
      alwaysShowInfoTooltip={alwaysShowInfoTooltip}
      className="indigo-flexible-date-picker"
    >
      <div
        className={clsx(
          "indigo-datepicker",
          "indigo-datepicker--filled",
          `indigo-datepicker--${size}`,
          className,
        )}
      >
        <AntDatePicker
          size={antSize}
          picker={spec.antPicker}
          value={date}
          defaultPickerValue={anchorDate ?? undefined}
          onChange={handleDateChange}
          onFocus={onFocus}
          className={date ? "has-value" : "no-value"}
          placeholder={placeholder}
          format={spec.format}
          status={isError ? "error" : ""}
          data-testid={dateTestId}
          classNames={{
            popup: {
              root: clsx("indigo-datepicker-dropdown", datePopupTestId),
            },
          }}
        />
      </div>
      <DropdownSelector<FlexibleDatePrecision>
        options={PRECISION_OPTIONS}
        value={precision}
        onChange={handlePrecisionChange}
        width="auto"
        size={size}
        // Three fixed options — a caret in the trigger reads as a text field.
        showSearch={false}
        className="indigo-flexible-date-picker-precision"
        data-testid={precisionTestId}
      />
    </LabelBlock>
  );
};

export default FlexibleDatePicker;
